import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    adminCreateAgent,
    AdminCreateAgentData,
    adminDeleteAgent,
    AdminDeleteAgentData,
    adminGetAgent,
    AdminGetAgentData,
    adminUpdateAgent,
    AdminUpdateAgentData,
} from "@/api";
import { appState } from "@/app/appState";
import { agent } from "@/app/typing/agents";
import { organisation_tree_node } from "@/app/typing/organisations";
import { Request } from "@/components/CRUD";
import { DefEdit, EntryItem, useRefState } from "@/components/UI/tools";
import { ActionHandler } from "@/components/UI/tools";
import { Back } from "@/components/UI/svgs";
import { EmptyRow, Row, Section } from "./ui";

/**
 * Mode B: the full prompt-set editor, plus the list of organisations this set is
 * the default for. Assignment itself lives on the organisation, since a child has
 * one default rather than a set having many independent assignments.
 */
export const PromptSetInspector = observer(({
    org,
    setId,
    usedBy,
    onBack,
    onSelectOrg,
    onChanged,
}: {
    org: organisation_tree_node;
    setId: number;
    usedBy: organisation_tree_node[];
    onBack: () => void;
    onSelectOrg: (id: number) => void;
    onChanged: (newSetId?: number) => void;
}) => {
    const [promptSet, setPromptSet] = useState<agent>();
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [edit, setEdit] = useState(false);
    const [refresh, setRefresh] = useState(0);

    const [name, setName] = useRefState<string>("");
    const [identity, setIdentity] = useRefState<string | undefined>(undefined);
    const [prePrompt, setPrePrompt] = useRefState<string | undefined>(undefined);
    const [summariserPrompt, setSummariserPrompt] = useRefState<string | undefined>(undefined);
    const [synthesiserPrompt, setSynthesiserPrompt] = useRefState<string | undefined>(undefined);
    const [documentSummariserPrompt, setDocumentSummariserPrompt] = useRefState<string | undefined>(undefined);
    const [documentSynthesiserPrompt, setDocumentSynthesiserPrompt] = useRefState<string | undefined>(undefined);
    const [lock, setLock] = useRefState(false);

    const isNew = setId === -1;

    const requiredFields = () =>
        name.current.length > 0 &&
        !!prePrompt.current &&
        prePrompt.current.length > 0 &&
        !!identity.current &&
        identity.current.length > 0;

    const load = Request<AdminGetAgentData, any>(
        adminGetAgent,
        () => ({ body: { id: setId, organisation_id: org.id } }),
        setLoading,
        (data: any) => setPromptSet(data.agent),
        () => {
            setPromptSet(undefined);
            appState.setModal({ title: appState.t("common.error"), body: appState.t("settings.prompt.errorOpen") });
        },
        () => !isNew,
    );

    const create = Request<AdminCreateAgentData, any>(
        adminCreateAgent,
        () => ({
            body: {
                name: name.current,
                identity: identity.current!,
                pre_prompt: prePrompt.current!,
                purpose: "legacy_field_placeholder",
                summariser_prompt: summariserPrompt.current,
                synthesiser_prompt: synthesiserPrompt.current,
                document_summariser_prompt: documentSummariserPrompt.current,
                document_synthesiser_prompt: documentSynthesiserPrompt.current,
                organisation_id: org.id,
            },
        }),
        setLoading,
        (data: any) => onChanged(data?.agent_id),
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("settings.prompt.errorCreate"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            }),
        requiredFields,
    );

    const update = Request<AdminUpdateAgentData, any>(
        adminUpdateAgent,
        () => ({
            body: {
                id: setId,
                name: name.current,
                identity: identity.current!,
                pre_prompt: prePrompt.current!,
                purpose: "legacy_field_placeholder",
                summariser_prompt: summariserPrompt.current,
                synthesiser_prompt: synthesiserPrompt.current,
                document_summariser_prompt: documentSummariserPrompt.current,
                document_synthesiser_prompt: documentSynthesiserPrompt.current,
                organisation_id: org.id,
            },
        }),
        setLoading,
        () => onChanged(setId),
        (error) => {
            setRefresh((p) => p + 1);
            setEdit(false);
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("settings.prompt.errorUpdate"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            });
        },
        () => !isNew && requiredFields(),
    );

    const remove = Request<AdminDeleteAgentData, any>(
        adminDeleteAgent,
        () => ({ body: { agent_id: setId, organisation_id: org.id } }),
        setDeleting,
        () => onBack(),
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("settings.prompt.errorDelete"),
            }),
        () => !isNew,
    );

    useEffect(() => {
        setPromptSet(undefined);
        setEdit(false);
        if (!isNew) load();
    }, [setId, org.id]);

    useEffect(() => {
        if (promptSet) {
            setLock(promptSet.user_lock ? !(promptSet.user_lock === appState.user?.id) : false);
            setName(promptSet.name);
            setIdentity(promptSet.identity);
            setPrePrompt(promptSet.pre_prompt);
            setSummariserPrompt(promptSet.summariser_prompt ?? "");
            setSynthesiserPrompt(promptSet.synthesiser_prompt ?? "");
            setDocumentSummariserPrompt(promptSet.document_summariser_prompt ?? "");
            setDocumentSynthesiserPrompt(promptSet.document_synthesiser_prompt ?? "");
        } else {
            setName("");
            setIdentity("");
            setPrePrompt("");
            setSummariserPrompt("");
            setSynthesiserPrompt("");
            setDocumentSummariserPrompt("");
            setDocumentSynthesiserPrompt("");
        }
        setLoading(false);
        setRefresh((p) => p + 1);
    }, [promptSet]);

    const assignedFromParent = promptSet !== undefined && promptSet.owned === false;
    const readonly = assignedFromParent || (org.permission_type < 4 && !isNew);

    if (!isNew && !(promptSet && promptSet.id === setId)) {
        return <p className="row-empty">{appState.t("common.loading")}</p>;
    }

    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-row items-center gap-[.5rem]">
                <ActionHandler title={appState.t("settings.prompt.back")} funq={onBack}>
                    <Back size={16} />
                </ActionHandler>
                <div className="inspector-header">
                    <p className="eyebrow">{appState.t("settings.prompt.set")}</p>
                    <p className="l truncate">
                        {org.name}
                        {assignedFromParent ? appState.t("settings.prompt.assignedReadonly") : ""}
                    </p>
                </div>
            </div>

            <EntryItem
                lock={lock.current}
                setEdit={(val: boolean) => DefEdit(val, isNew ? undefined : setId, setEdit, "agent", org.id)}
                edit={edit}
                name="prompt set"
                loading={loading}
                deleting={deleting}
                readonly={readonly}
                refreshCount={refresh}
                idParams={{
                    singular: { get: setId, set: () => onBack() },
                    plural: { get: [], set: () => { } },
                }}
                createEntry={create}
                updateEntry={update}
                deleteEntry={remove}
                nameColumn={{
                    type: "text",
                    value: name.current,
                    onChange: setName,
                    validator: (val: string) => /^[\p{L}0-9_\s-]+$/u.test(val),
                    label: appState.t("objects.variableName"),
                }}
                refColumns={[
                    {
                        label: appState.t("settings.prompt.identityLabel"),
                        type: "richtext",
                        validator: (value: string | undefined) => (value ? true : false),
                        value: identity.current,
                        onChange: setIdentity,
                        artifactsComplete: true,
                    },
                    {
                        label: appState.t("settings.prompt.prePromptLabel"),
                        type: "richtext",
                        validator: (value: string | undefined) => (value ? true : false),
                        value: prePrompt.current,
                        onChange: setPrePrompt,
                        artifactsComplete: true,
                    },
                    {
                        label: appState.t("settings.prompt.summariserLabel"),
                        type: "richtext",
                        validator: () => true,
                        value: summariserPrompt.current,
                        onChange: setSummariserPrompt,
                        artifactsComplete: false,
                    },
                    {
                        label: appState.t("settings.prompt.synthesiserLabel"),
                        type: "richtext",
                        validator: () => true,
                        value: synthesiserPrompt.current,
                        onChange: setSynthesiserPrompt,
                        artifactsComplete: false,
                    },
                    {
                        label: appState.t("settings.prompt.documentSummariserLabel"),
                        type: "richtext",
                        validator: () => true,
                        value: documentSummariserPrompt.current,
                        onChange: setDocumentSummariserPrompt,
                        artifactsComplete: false,
                    },
                    {
                        label: appState.t("settings.prompt.documentSynthesiserLabel"),
                        type: "richtext",
                        validator: () => true,
                        value: documentSynthesiserPrompt.current,
                        onChange: setDocumentSynthesiserPrompt,
                        artifactsComplete: false,
                    },
                ]}
            />

            {!isNew && (
                <Section title={appState.t("settings.prompt.inUse")} hint={usedBy.length > 0 ? `${usedBy.length}` : undefined}>
                    {usedBy.length === 0 && <EmptyRow>Not assigned anywhere you can see.</EmptyRow>}
                    {usedBy.map((used) => (
                        <Row key={used.id} onClick={() => onSelectOrg(used.id)} chevron>
                            <p className="l truncate">{used.name}</p>
                        </Row>
                    ))}
                </Section>
            )}
        </div>
    );
});
